import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import PaperDraggable from "../../../../../../components/PaperDraggable"
import { IDataDetalheExpItem } from "../../../../../../types/api/estoque/detalhar-expedicao"
import { useEffect, useMemo, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import axiosClient from "../../../../../../lib/axios"
import { enqueueSnackbar } from "notistack"
import { IAttExpedicaoItemInput } from "../../../../../../types/mutations/atualizar-expedicao-item"
import { useFormContext } from "react-hook-form"
import tratativaDeDatas from "../../../../../../helpers/tratativaDeDatas"

export default function DialogEditPedidoSeparacao({
    open,
    handleClose
}: DialogEditPedidoSeparacaoProps) {

    const { setValue, watch } = useFormContext()
    const { produto } = open
    const queryClient = useQueryClient();
    //valores importantes
    const itens = watch('itens')
    const possuiLotes = produto.exige_lote
    const itemIndex = itens.findIndex((item: IDataDetalheExpItem) => item.id === open.id);

    //

    //funções manipuladoras
    const handleCheckLoteValue = (): IListaLotes[] => {
        if (!possuiLotes || !produto.lotes_selecionados) return []


        return produto.lotes_disponiveis.map((lote) => {
            if (!produto.lotes_selecionados) return { ...lote, quantidade: "0" }

            const selectedLote = produto.lotes_selecionados.find((selected) => selected.lote === lote.lote);
            return {
                lote: lote.lote,
                vencimento: lote.vencimento,
                estoque_atual: lote.estoque_atual,
                quantidade: selectedLote ? selectedLote.quantidade.toString() : "0",
            };
        })
    }

    const handleUpdateQuantidade = (lote: string, quantidade: string) => {
        setListaLotes((prevListaLotes) => {
            return prevListaLotes.map((loteItem) => {
                if (loteItem.lote === lote) {
                    return { ...loteItem, quantidade };
                }
                return loteItem;
            });
        });
    };

    const handleSubmitValues = () => {
        if (quantidade === "NaN" || quantidade === "") {
            enqueueSnackbar(
                'Favor incluir apenas valores númericos nos campos de quantidades',
                { variant: 'error' }
            )
            return
        }

        const formatQtd = Number(quantidade)

        if (formatQtd > produto.estoque_atual) {
            enqueueSnackbar(
                'A quantidade a separar não pode ser maior que o estoque atual',
                { variant: 'warning' }
            )
            return
        }

        const formatLotes = listaLotes.filter((lote) => lote.lote !== "" && lote.quantidade !== "0")
            .map((lote) => ({
                lote: lote.lote,
                quantidade: parseInt(lote.quantidade),
            }));

        setValue(`itens[${itemIndex}].quantidade`, formatQtd)
        setValue(`itens[${itemIndex}].lotes_indicados`, formatLotes)

        attExpedicaoItem({
            quantidade: formatQtd,
            lotes: formatLotes
        })
    }
    //

    const [mostrarTodos, setMostrarTodos] = useState<boolean>(false)
    const [quantidade, setQuantidade] = useState<string>(watch(`itens[${itemIndex}].quantidade`).toString())
    const [listaLotes, setListaLotes] = useState<IListaLotes[]>(handleCheckLoteValue())

    const listagem: IListaLotes[] = useMemo(() => {
        if (mostrarTodos) return listaLotes

        return listaLotes.filter((lote) => lote.estoque_atual !== 0 || (Number(lote.quantidade) > 0 || lote.quantidade === ""))
    }, [mostrarTodos, listaLotes])

    //queries e mutations
    const { mutate: attExpedicaoItem, isLoading } = useMutation({
        mutationKey: "atualizar-expedicao-item",
        mutationFn: (inputs: IAttExpedicaoItemInput) => axiosClient.put("/estoque/atualizar-expedicao-item/" + open.id, inputs),
        onError: (e) => {
            console.error(e)
            enqueueSnackbar('Erro ao atualizar item da expedição', { variant: 'error' });
        },
        onSuccess: () => {
            enqueueSnackbar('Item da expedição atualizado com sucesso', { variant: 'success' });
            queryClient.invalidateQueries("pesquisar-itens-para-expedir");
            queryClient.invalidateQueries("dialog-detalhar-expedicoes");
            handleClose()

        }
    });
    //

    useEffect(() => {
        if (possuiLotes) {
            const somaQuantidade = listaLotes.reduce((acc, current) => acc + parseInt(current.quantidade), 0);
            setQuantidade(somaQuantidade.toString())
        }
    }, [listaLotes])

    return (
        <Dialog
            fullWidth
            maxWidth={possuiLotes ? "sm" : "xs"}
            open={Boolean(open)}
            onClose={handleClose}
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                Editar item
            </DialogTitle>

            <DialogContent>
                <Box>
                    <Typography fontWeight="bold">{produto.nome}</Typography>
                    <Typography variant="caption">Cód.: {produto.codigo} / Setor: {produto.setor}</Typography>
                    <Typography variant="caption"> / Estoque: {produto.estoque_atual}</Typography>
                </Box>

                <TextField
                    margin="normal"
                    fullWidth
                    disabled={possuiLotes}
                    value={quantidade}
                    type="number"
                    onKeyDown={(event) => {
                        if (event.key === "-" || event.key === "+" || event.key === "e") {
                            event.preventDefault();
                        }
                    }}
                    onChange={({ target }) => {
                        const valor = target.value === "" ? "" : parseInt(target.value, 10);
                        if (valor === "" || valor >= 0) {
                            setQuantidade(target.value);
                        }
                    }}
                    slotProps={{ htmlInput: { min: 0 } }}
                    label="Quantidade"
                    variant="outlined"
                />

                {
                    (possuiLotes) &&
                    <>
                        <FormControlLabel
                            control={<Switch value={mostrarTodos} onChange={() => setMostrarTodos(prevState => !prevState)} />}
                            label="Mostrar lotes sem disponibilidade"
                            slotProps={{
                                typography: { variant: "subtitle2" }
                            }}
                        />

                        <TableContainer sx={{ height: 300, overflow: "auto", pr: 1 }} >
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Lotes</b></TableCell>
                                        <TableCell align="center"><b>Vencimento</b></TableCell>
                                        <TableCell align="right"><b>Disponível</b></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {listagem.map((row) => (
                                        <TableRow
                                            key={row.lote}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell>
                                                {row.lote}
                                            </TableCell>

                                            <TableCell align="center">
                                                {tratativaDeDatas(row.vencimento)}
                                            </TableCell>

                                            <TableCell align="right">
                                                {row.estoque_atual}
                                            </TableCell>

                                            <TableCell align="right">
                                                <TextField
                                                    fullWidth
                                                    value={row.quantidade}
                                                    label="Qtd"
                                                    variant="outlined"
                                                    type="number"
                                                    onChange={({ target }) => {
                                                        const value = parseInt(target.value, 10);
                                                        if (value >= 0 || target.value === "") {
                                                            handleUpdateQuantidade(row.lote, value.toString());
                                                        }
                                                    }}
                                                    slotProps={{ htmlInput: { min: 0 } }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                }
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                >
                    Cancelar
                </Button>
                <Button
                    disabled={isLoading}
                    autoFocus
                    variant="contained"
                    onClick={handleSubmitValues}
                >
                    {
                        isLoading &&
                        <CircularProgress size={20} sx={{ mr: 1.5 }} />
                    }
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogEditPedidoSeparacaoProps {
    open: IDataDetalheExpItem,
    handleClose: () => void
}

interface IListaLotes {
    lote: string,
    vencimento: string,
    estoque_atual: number,
    quantidade: string,
}